'use client';

import React, { useState, useEffect } from 'react';
import CaseStudyCard from './CaseStudyCard';
import FilterBar from './FilterBar';
import StatsOverview from './StatsOverview';
import TestimonialSection from './TestimonialSection';
import CTASection from './CTASection';
import case1 from '../../../../public/assets/images/case-study/case1.png'
import case2 from '../../../../public/assets/images/case-study/case2.png'
import case3 from '../../../../public/assets/images/case-study/case3.png'
import case4 from '../../../../public/assets/images/case-study/case4.png'
import case5 from '../../../../public/assets/images/case-study/case5.png'
import case6 from '../../../../public/assets/images/case-study/case6.png'
import case7 from '../../../../public/assets/images/case-study/case7.png'
import case8 from '../../../../public/assets/images/case-study/case8.png'
import case9 from '../../../../public/assets/images/case-study/case9.png'

interface CaseStudy {
  id: string;
  title: string;
  client: string;
  industry: string;
  image: string;
  alt: string;
  challenge: string;
  solution: string;
  results: {
    metric: string;
    value: string;
    icon: string;
  }[];
  technologies: string[];
  duration: string;
  teamSize: string;
  outcomeType: string;
}

interface Testimonial {
  id: string;
  name: string;
  role: string;
  company: string;
  image: string;
  alt: string;
  quote: string;
  caseStudyId: string;
}

interface Stat {
  label: string;
  value: string;
  icon: string;
  color: string;
}

const CaseStudiesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedTechnology, setSelectedTechnology] = useState('');
  const [selectedOutcome, setSelectedOutcome] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const caseStudies: CaseStudy[] = [
  {
    id: '1',
    title: 'Enterprise Cloud Migration',
    client: 'Global Financial Services Corp',
    industry: 'Finance',
    image:case1.src,
    alt: 'Modern office workspace with multiple computer monitors displaying financial data charts and graphs',
    challenge: 'Legacy on-premise infrastructure causing scalability issues and high maintenance costs with limited disaster recovery capabilities.',
    solution: 'Implemented comprehensive AWS cloud migration strategy with containerized microservices architecture and automated CI/CD pipelines.',
    results: [
    { metric: 'Cost Reduction', value: '45%', icon: 'ArrowTrendingDownIcon' },
    { metric: 'Uptime', value: '99.9%', icon: 'CheckCircleIcon' },
    { metric: 'Deploy Speed', value: '10x', icon: 'RocketLaunchIcon' }],

    technologies: ['AWS', 'Docker', 'Kubernetes', 'Terraform', 'Jenkins'],
    duration: '8 months',
    teamSize: '12 engineers',
    outcomeType: 'Cost Optimization'
  },
  {
    id: '2',
    title: 'Healthcare Data Platform',
    client: 'MediCare Solutions',
    industry: 'Healthcare',
    image:case2.src,
    alt: 'Healthcare professional in white coat using tablet device in modern medical facility',
    challenge: 'Fragmented patient data across multiple systems leading to inefficient care coordination and compliance risks.',
    solution: 'Built HIPAA-compliant unified data platform with real-time analytics and secure API integrations across all healthcare systems.',
    results: [
    { metric: 'Data Access', value: '80%', icon: 'ClockIcon' },
    { metric: 'Compliance', value: '100%', icon: 'ShieldCheckIcon' },
    { metric: 'Patient Satisfaction', value: '35%', icon: 'HeartIcon' }],

    technologies: ['Azure', 'FHIR', 'React', 'Node.js', 'PostgreSQL'],
    duration: '10 months',
    teamSize: '15 engineers',
    outcomeType: 'Compliance Achievement'
  },
  {
    id: '3',
    title: 'E-Commerce Platform Modernization',
    client: 'RetailMax International',
    industry: 'Retail',
    image:case3.src ,
    alt: 'Modern retail store interior with digital displays and customers shopping with mobile devices',
    challenge: 'Outdated e-commerce platform unable to handle peak traffic, resulting in lost sales and poor customer experience.',
    solution: 'Redesigned platform with headless commerce architecture, progressive web app, and intelligent caching strategies.',
    results: [
    { metric: 'Revenue Growth', value: '125%', icon: 'CurrencyDollarIcon' },
    { metric: 'Page Load', value: '2.1s', icon: 'BoltIcon' },
    { metric: 'Conversion', value: '68%', icon: 'ArrowTrendingUpIcon' }],

    technologies: ['Next.js', 'Shopify Plus', 'Redis', 'GraphQL', 'Vercel'],
    duration: '6 months',
    teamSize: '10 engineers',
    outcomeType: 'Revenue Growth'
  },
  {
    id: '4',
    title: 'Manufacturing IoT Integration',
    client: 'TechManufacturing Inc',
    industry: 'Manufacturing',
    image:case4.src,
    alt: 'Industrial manufacturing floor with robotic arms and automated assembly line equipment',
    challenge: 'Manual production monitoring leading to inefficiencies, quality issues, and inability to predict equipment failures.',
    solution: 'Deployed IoT sensor network with real-time monitoring dashboard and predictive maintenance AI algorithms.',
    results: [
    { metric: 'Downtime', value: '60%', icon: 'ArrowTrendingDownIcon' },
    { metric: 'Quality', value: '40%', icon: 'CheckBadgeIcon' },
    { metric: 'Efficiency', value: '55%', icon: 'ChartBarIcon' }],

    technologies: ['Azure IoT', 'Python', 'TensorFlow', 'Power BI', 'MQTT'],
    duration: '12 months',
    teamSize: '18 engineers',
    outcomeType: 'Efficiency Improvement'
  },
  {
    id: '5',
    title: 'Educational Platform Development',
    client: 'EduTech Academy',
    industry: 'Education',
    image:case5.src,
    alt: 'Students in modern classroom using laptops and tablets for digital learning',
    challenge: 'Traditional learning management system lacking engagement features and unable to support hybrid learning models.',
    solution: 'Created interactive learning platform with video conferencing, gamification, and adaptive learning paths.',
    results: [
    { metric: 'Engagement', value: '85%', icon: 'AcademicCapIcon' },
    { metric: 'Completion Rate', value: '72%', icon: 'TrophyIcon' },
    { metric: 'User Growth', value: '200%', icon: 'UserGroupIcon' }],

    technologies: ['React', 'WebRTC', 'MongoDB', 'Socket.io', 'AWS'],
    duration: '9 months',
    teamSize: '14 engineers',
    outcomeType: 'User Growth'
  },
  {
    id: '6',
    title: 'Logistics Optimization System',
    client: 'FastShip Logistics',
    industry: 'Logistics',
    image:case6.src,
    alt: 'Large warehouse with organized inventory shelves and automated sorting systems',
    challenge: 'Inefficient route planning and inventory management causing delays and increased operational costs.',
    solution: 'Implemented AI-powered route optimization and real-time inventory tracking with predictive demand forecasting.',
    results: [
    { metric: 'Delivery Time', value: '35%', icon: 'TruckIcon' },
    { metric: 'Fuel Costs', value: '28%', icon: 'ArrowTrendingDownIcon' },
    { metric: 'Accuracy', value: '95%', icon: 'CheckCircleIcon' }],

    technologies: ['Google Maps API', 'Python', 'PostgreSQL', 'React', 'Docker'],
    duration: '7 months',
    teamSize: '11 engineers',
    outcomeType: 'Cost Optimization'
  }];


  const testimonials: Testimonial[] = [
  {
    id: '1',
    name: 'Sarah Johnson',
    role: 'CTO',
    company: 'Global Financial Services Corp',
    image:case7.src,
    alt: 'Professional woman with short brown hair in navy business suit smiling confidently',
    quote: 'ApexTechCraft transformed our infrastructure completely. The cloud migration was seamless, and we\'ve seen dramatic improvements in both performance and cost efficiency. Their team\'s expertise and dedication exceeded all expectations.',
    caseStudyId: '1'
  },
  {
    id: '2',
    name: 'Dr. Michael Chen',
    role: 'Chief Medical Information Officer',
    company: 'MediCare Solutions',
    image:case8.src,
    alt: 'Asian male doctor in white medical coat with stethoscope in modern hospital setting',
    quote: 'The healthcare data platform has revolutionized how we deliver patient care. Having unified, real-time access to patient information has improved our care coordination dramatically while maintaining the highest security standards.',
    caseStudyId: '2'
  },
  {
    id: '3',
    name: 'Jennifer Martinez',
    role: 'VP of Digital Commerce',
    company: 'RetailMax International',
    image:case9.src,
    alt: 'Hispanic woman with long dark hair in professional burgundy blazer smiling warmly',
    quote: 'Our new e-commerce platform has been a game-changer. The performance improvements are incredible, and our customers love the experience. The revenue growth speaks for itself - this partnership has been invaluable.',
    caseStudyId: '3'
  }];


  const stats: Stat[] = [
  {
    label: 'Successful Projects',
    value: '150+',
    icon: 'CheckBadgeIcon',
    color: 'bg-success'
  },
  {
    label: 'Client Satisfaction',
    value: '98%',
    icon: 'HeartIcon',
    color: 'bg-primary'
  },
  {
    label: 'Average ROI',
    value: '285%',
    icon: 'ChartBarIcon',
    color: 'bg-accent'
  },
  {
    label: 'Industries Served',
    value: '12+',
    icon: 'BuildingOfficeIcon',
    color: 'bg-trust'
  }];


  const industries = [...new Set(caseStudies.map((study) => study.industry))];
  const technologies = [
  ...new Set(caseStudies.flatMap((study) => study.technologies))];

  const outcomes = [...new Set(caseStudies.map((study) => study.outcomeType))];

  const filteredStudies = isHydrated ?
  caseStudies.filter((study) => {
    const matchesIndustry = !selectedIndustry || study.industry === selectedIndustry;
    const matchesTechnology =
    !selectedTechnology || study.technologies.includes(selectedTechnology);
    const matchesOutcome = !selectedOutcome || study.outcomeType === selectedOutcome;
    const matchesSearch =
    !searchQuery ||
    study.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    study.client.toLowerCase().includes(searchQuery.toLowerCase()) ||
    study.challenge.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesIndustry && matchesTechnology && matchesOutcome && matchesSearch;
  }) :
  caseStudies;

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:269:6" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="269" data-component-end-line="286" data-component-file="CaseStudiesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:270:8" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="270" data-component-end-line="285" data-component-file="CaseStudiesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20py-16%22%7D" className="container mx-auto px-4 py-16">
          <div data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:271:10" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="271" data-component-end-line="278" data-component-file="CaseStudiesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
            <h1 data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:272:12" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="272" data-component-end-line="274" data-component-file="CaseStudiesInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-4xl%20md%3Atext-5xl%20font-heading%20font-bold%20text-secondary%20mb-4%22%2C%22textContent%22%3A%22Case%20Studies%22%7D" className="text-4xl md:text-5xl font-heading font-bold text-secondary mb-4">
              Case Studies
            </h1>
            <p data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:275:12" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="275" data-component-end-line="277" data-component-file="CaseStudiesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-muted-foreground%20max-w-3xl%20mx-auto%22%2C%22textContent%22%3A%22Explore%20our%20portfolio%20of%20successful%20digital%20transformation%20projects%22%7D" className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Explore our portfolio of successful digital transformation projects
            </p>
          </div>
          <StatsOverview data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:279:10" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="279" data-component-end-line="279" data-component-file="CaseStudiesInteractive.tsx" data-component-name="StatsOverview" data-component-content="%7B%22elementName%22%3A%22StatsOverview%22%7D" stats={stats} />
          <div data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:280:10" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="280" data-component-end-line="284" data-component-file="CaseStudiesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {caseStudies.slice(0, 6).map((study) =>
            <CaseStudyCard data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:282:12" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="282" data-component-end-line="282" data-component-file="CaseStudiesInteractive.tsx" data-component-name="CaseStudyCard" data-component-content="%7B%22elementName%22%3A%22CaseStudyCard%22%7D" key={study.id} study={study} />
            )}
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:291:4" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="291" data-component-end-line="349" data-component-file="CaseStudiesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <div data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:292:6" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="292" data-component-end-line="348" data-component-file="CaseStudiesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20py-16%22%7D" className="container mx-auto px-4 py-16">
        <div data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:293:8" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="293" data-component-end-line="302" data-component-file="CaseStudiesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
          <h1 data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:294:10" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="294" data-component-end-line="296" data-component-file="CaseStudiesInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-4xl%20md%3Atext-5xl%20font-heading%20font-bold%20text-secondary%20mb-4%22%2C%22textContent%22%3A%22Case%20Studies%22%7D" className="text-4xl md:text-5xl font-heading font-bold text-secondary mb-4">
            Case Studies
          </h1>
          <p data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:297:10" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="297" data-component-end-line="301" data-component-file="CaseStudiesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-muted-foreground%20max-w-3xl%20mx-auto%22%2C%22textContent%22%3A%22Explore%20our%20portfolio%20of%20successful%20digital%20transformation%20projects%20across%20diverse%5Cn%20%20%20%20%20%20%20%20%20%20%20%20industries.%20Each%20case%20study%20demonstrates%20our%20commitment%20to%20delivering%20measurable%5Cn%20%20%20%20%20%20%20%20%20%20%20%20results%20and%20building%20lasting%20partnerships.%22%7D" className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Explore our portfolio of successful digital transformation projects across diverse
            industries. Each case study demonstrates our commitment to delivering measurable
            results and building lasting partnerships.
          </p>
        </div>

        <StatsOverview data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:304:8" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="304" data-component-end-line="304" data-component-file="CaseStudiesInteractive.tsx" data-component-name="StatsOverview" data-component-content="%7B%22elementName%22%3A%22StatsOverview%22%7D" stats={stats} />

        <FilterBar data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:306:8" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="306" data-component-end-line="317" data-component-file="CaseStudiesInteractive.tsx" data-component-name="FilterBar" data-component-content="%7B%22elementName%22%3A%22FilterBar%22%7D"
          selectedIndustry={selectedIndustry}
          selectedTechnology={selectedTechnology}
          selectedOutcome={selectedOutcome}
          searchQuery={searchQuery}
          onIndustryChange={setSelectedIndustry}
          onTechnologyChange={setSelectedTechnology}
          onOutcomeChange={setSelectedOutcome}
          onSearchChange={setSearchQuery}
          industries={industries}
          technologies={technologies}
          outcomes={outcomes} />


        {filteredStudies.length === 0 ?
        <div data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:321:8" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="321" data-component-end-line="336" data-component-file="CaseStudiesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-16%22%7D" className="text-center py-16">
            <p data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:322:12" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="322" data-component-end-line="324" data-component-file="CaseStudiesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xl%20text-muted-foreground%20mb-4%22%2C%22textContent%22%3A%22No%20case%20studies%20match%20your%20current%20filters%22%7D" className="text-xl text-muted-foreground mb-4">
              No case studies match your current filters
            </p>
            <button data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:325:12" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="325" data-component-end-line="335" data-component-file="CaseStudiesInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-6%20py-3%20bg-primary%20text-primary-foreground%20rounded-md%20font-semibold%20hover%3Abg-primary%2F90%20transition-colors%20duration-200%22%2C%22textContent%22%3A%22Clear%20All%20Filters%22%7D"
            onClick={() => {
              setSelectedIndustry('');
              setSelectedTechnology('');
              setSelectedOutcome('');
              setSearchQuery('');
            }}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-md font-semibold hover:bg-primary/90 transition-colors duration-200">

              Clear All Filters
            </button>
          </div> :

        <div data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:338:8" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="338" data-component-end-line="342" data-component-file="CaseStudiesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%20mb-12%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {filteredStudies.map((study) =>
          <CaseStudyCard data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:340:10" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="340" data-component-end-line="340" data-component-file="CaseStudiesInteractive.tsx" data-component-name="CaseStudyCard" data-component-content="%7B%22elementName%22%3A%22CaseStudyCard%22%7D" key={study.id} study={study} />
          )}
          </div>
        }

        <TestimonialSection data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:345:8" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="345" data-component-end-line="345" data-component-file="CaseStudiesInteractive.tsx" data-component-name="TestimonialSection" data-component-content="%7B%22elementName%22%3A%22TestimonialSection%22%7D" testimonials={testimonials} />

        <CTASection data-component-id="src/app/case-studies/components/CaseStudiesInteractive.tsx:347:8" data-component-path="src/app/case-studies/components/CaseStudiesInteractive.tsx" data-component-line="347" data-component-end-line="347" data-component-file="CaseStudiesInteractive.tsx" data-component-name="CTASection" data-component-content="%7B%22elementName%22%3A%22CTASection%22%7D" />
      </div>
    </div>);

};

export default CaseStudiesInteractive;